/**
 * 초기화와 화면 연결.
 *
 * 순서가 중요하다 — Python 런타임 적재가 끝나기 전에는 연결 버튼을 켜지 않는다.
 * Serial 문제와 Python 문제를 섞어서 보여 주지 않기 위해서다.
 */
import "./style.css";

import { WebCarEngine } from "./engine/web-car-engine";
import { realClock } from "./engine/clock";
import { InputController } from "./input/controller";
import { PythonBridge, readCatalog } from "./python/bridge";
import { loadRuntime, RuntimeError } from "./python/runtime";
import {
  NEOPIA_DONGLE_FILTER,
  getSerial,
  isSupported,
  type PortLike,
} from "./serial/transport";
import { Ui } from "./ui/render";

function describePortOption(port: PortLike, index: number): string {
  const info = port.getInfo();
  // COM 번호·장치 파일 경로를 브라우저가 알려 주지는 않는다.
  const vendor = info.usbVendorId === undefined ? "?" : `0x${info.usbVendorId.toString(16)}`;
  const product = info.usbProductId === undefined ? "?" : `0x${info.usbProductId.toString(16)}`;
  return `포트 ${index + 1} — VID ${vendor} / PID ${product}`;
}

async function main(): Promise<void> {
  const boot = document.getElementById("boot")!;

  if (!isSecureContext) {
    boot.textContent =
      "HTTPS 또는 localhost 에서 열어야 합니다. 이 페이지는 장치에 연결할 수 없습니다.";
    return;
  }
  if (!isSupported()) {
    boot.textContent =
      "이 브라우저는 Web Serial 을 지원하지 않습니다. 데스크톱 Chrome 또는 Edge 를 쓰세요.";
    return;
  }
  if (window.top !== window.self) {
    boot.textContent = "iframe 안에서는 장치에 연결하지 않습니다. 최상위 페이지로 여세요.";
    return;
  }

  boot.textContent = "Python 런타임을 준비하는 중…";
  let runtime;
  try {
    runtime = await loadRuntime((step, done, total) => {
      boot.textContent = `${step} (${done}/${total})`;
    });
  } catch (error) {
    const message = error instanceof RuntimeError ? error.message : String(error);
    const hint = error instanceof RuntimeError ? error.hint : "";
    boot.textContent = `준비 실패: ${message}\n${hint}`;
    return;
  }

  const catalog = readCatalog(runtime);
  const engine = new WebCarEngine({
    catalog,
    clock: realClock,
    createBridge: (productId, options) => PythonBridge.create(runtime, productId, options),
    onChange: (view) => ui.schedule(view),
  });

  const controller = new InputController({
    engine,
    onError: (error) => reportError(error),
  });

  const serial = getSerial()!;
  let knownPorts: PortLike[] = [];

  const ui = new Ui({
    engine,
    controller,
    onRequestPort: (filtered) => {
      // 사용자 동작 안에서 곧바로 부른다 — 다른 비동기 작업을 먼저 기다리지 않는다.
      const options = filtered ? { filters: [NEOPIA_DONGLE_FILTER] } : {};
      try {
        engine.beginPortSelection();
      } catch (error) {
        reportError(error);
        return;
      }
      serial
        .requestPort(options)
        .then((port) => engine.connect(port))
        .then(() => refreshKnownPorts())
        .catch((error) => {
          engine.endPortSelection();
          // 선택창 취소는 오류가 아니다 — idle 로 돌아가기만 한다.
          if (error instanceof DOMException && error.name === "NotFoundError") return;
          reportError(error);
        });
    },
    onConnectKnown: (index) => {
      const port = knownPorts[index];
      if (!port) return;
      engine.connect(port).catch(reportError);
    },
    onError: (error) => reportError(error),
  });

  function reportError(error: unknown): void {
    const message = error instanceof Error ? error.message : String(error);
    boot.hidden = false;
    boot.textContent = message;
    window.setTimeout(() => {
      boot.hidden = true;
    }, 4000);
  }

  async function refreshKnownPorts(): Promise<void> {
    knownPorts = await serial.getPorts();
    ui.setKnownPorts(knownPorts.map(describePortOption));
  }

  serial.addEventListener("disconnect", (event) => {
    const port = (event as unknown as { target: PortLike }).target;
    void engine.handlePortDisconnected(port);
    void refreshKnownPorts();
  });
  serial.addEventListener("connect", () => {
    void refreshKnownPorts();
  });

  controller.attach();
  await refreshKnownPorts();

  ui.showApp();
  engine.markReady();
  ui.drawNow(engine.view());

  // 같은 페이지에 엔진은 하나뿐이다. 개발용 디버그 전역을 배포 경로에 남기지 않는다.
  if (import.meta.env.DEV) {
    Object.assign(window, { __neocarEngine: engine, __neocarRuntime: runtime });
  }
}

void main();
